import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Turnstile } from '@/components/Turnstile';
import { authClient, captureTokenFromResponse } from '@/lib/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Allow other Pure surfaces (e.g. the management dashboard) to send unauthed
  // visitors here with `?return=<absolute-url>` and have us bounce back after
  // sign-in. Same-origin paths use react-router; off-host URLs (different
  // port like :8090) need a hard navigation so the cookie travels.
  const returnUrl = searchParams.get('return') ?? '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await authClient.signIn.email({
        email,
        password,
      });
      if (res.error) {
        setError(res.error.message ?? 'Login failed');
        return;
      }
      // Better Auth's bearer plugin puts the token in the `set-auth-token` header.
      // captureTokenFromResponse expects a Headers object from an underlying fetch.
      // The client response doesn't expose raw headers directly — fall back to the
      // data.token property which bearer plugin includes.
      const token = (res.data as unknown as { token?: string })?.token ?? null;
      if (typeof token !== 'string') {
        setError('login succeeded but no token was returned');
        return;
      }
      const { setToken } = await import('@/lib/sw');
      await setToken(token);
      // Force a full navigation (not react-router) when the return URL is:
      //  1. cross-origin (different host/port — cookie needs to land there)
      //  2. a server-side route (/api/*) — react-router doesn't match those
      //     and would fall back to /dashboard.  This is the management.pure.dev
      //     SSO bounce path: app.pure.dev/login?return=
      //     https%3A%2F%2Fapp.pure.dev%2Fapi%2Fauth%2Fsso%2Fissue%3F...
      //     is same-origin but lives on the server, not the SPA.
      //  3. any absolute URL the caller passed — treat as "go there literally."
      // Otherwise it's an internal SPA path, react-router can handle it.
      try {
        const u = new URL(returnUrl, window.location.origin);
        const isCrossOrigin = u.origin !== window.location.origin;
        const isServerRoute = u.pathname.startsWith('/api/');
        const isAbsolute    = /^https?:/i.test(returnUrl);
        if (isCrossOrigin || isServerRoute || isAbsolute) {
          window.location.assign(u.toString());
          return;
        }
      } catch { /* not a URL — treat as internal path */ }
      navigate(returnUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function signInWith(provider: 'github' | 'google') {
    setError(null);
    try {
      // Pass the return URL through the OAuth flow so the cross-subdomain
      // SSO bounce (management → app login → back to management) works for
      // OAuth users too — not just email/password.  better-auth supports
      // arbitrary callbackURL; the server-side handler validates it against
      // the trusted origin allowlist.
      await authClient.signIn.social({ provider, callbackURL: returnUrl });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OAuth redirect failed');
    }
  }

  // Reference turnstileToken to silence unused warning — the token is sent by
  // the SW-less form via the global <Turnstile> widget which stashes it on
  // cf-turnstile-response cookie/header; for now we keep the capture here for
  // future explicit wiring.
  void turnstileToken;

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in to Pure</CardTitle>
          <CardDescription>Compile your code to native binaries</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => signInWith('github')} type="button">
              GitHub
            </Button>
            <Button variant="outline" onClick={() => signInWith('google')} type="button">
              Google
            </Button>
          </div>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or</span>
            </div>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Turnstile onToken={setTurnstileToken} />
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <div className="text-sm text-muted-foreground text-center">
            <Link to="/reset-password" className="underline-offset-4 hover:underline">
              Forgot your password?
            </Link>
          </div>
          <div className="text-sm text-center">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="font-medium underline-offset-4 hover:underline">
              Sign up
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
